import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BackgroundCanvas } from '../components/BackgroundCanvas';
import { fetchBackendVersion } from '../api/version';
import { BUILD_VERSION } from '../buildVersion';


type Tab = 'about' | 'help' | 'contacts' | 'oferta' | 'privacy';

const TABS: Tab[] = ['about', 'help', 'contacts', 'oferta', 'privacy'];

export const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TABS.includes(searchParams.get('tab') as Tab)
    ? (searchParams.get('tab') as Tab)
    : 'about';
  const [tab, setTab] = useState<Tab>(initialTab);
  const [backendVersion, setBackendVersion] = useState<string | null>(null);

  useEffect(() => {
    fetchBackendVersion().then(setBackendVersion).catch(() => {});
  }, []);

  const handleTabChange = (key: Tab) => {
    setTab(key);
    setSearchParams({ tab: key }, { replace: true });
  };

  return (
    <div className="auth-page">
      <BackgroundCanvas opacity={0.12} />

      <div className="term-panel" style={{ maxWidth: 600, width: '100%' }}>
        <div className="term-panel__bar">
          <span className="term-panel__bar-dot" />
          <span className="term-panel__bar-dot" />
          <span className="term-panel__bar-dot" />
          <span className="term-panel__bar-title">{t('about.termTitle')}</span>
        </div>

        <div className="term-panel__body">
          <h1 className="auth-form__heading">{t('about.heading')}</h1>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            {TABS.map(key => (
              <button
                key={key}
                className="btn-transparent"
                style={{
                  opacity: tab === key ? 1 : 0.5,
                  borderBottom: tab === key ? '1px solid var(--px-accent)' : '1px solid transparent',
                  borderRadius: 0,
                  padding: '2px 10px',
                  fontSize: '0.6rem',
                  letterSpacing: '1px',
                }}
                onClick={() => handleTabChange(key)}
              >
                {t(`about.tabs.${key}`)}
              </button>
            ))}
          </div>

          {/* About */}
          {tab === 'about' && (
            <div style={{ fontSize: '0.75rem', lineHeight: '1.7', color: 'var(--px-text)' }}>
              <p style={{ marginBottom: '1rem' }}>{t('about.desc1')}</p>
              <p style={{ marginBottom: '1rem' }}>{t('about.desc2')}</p>
              <p style={{ marginBottom: '1rem' }}>{t('about.desc3')}</p>
              <ul style={{ paddingLeft: '1.25rem', marginBottom: '1rem' }}>
                <li>{t('about.feat1')}</li>
                <li>{t('about.feat2')}</li>
                <li>{t('about.feat3')}</li>
                <li>{t('about.feat4')}</li>
              </ul>
              <p style={{ color: 'var(--px-text-dim)', fontSize: '0.62rem', fontFamily: 'monospace', marginTop: '1.5rem' }}>
                frontend v{BUILD_VERSION}
                {backendVersion && <> · backend v{backendVersion}</>}
              </p>
            </div>
          )}

          {/* Help */}
          {tab === 'help' && (
            <div style={{ fontSize: '0.72rem', lineHeight: '1.7', color: 'var(--px-text)', maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.5rem' }}>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.6rem', letterSpacing: '1px', marginBottom: '0.6rem', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.12)', paddingBottom: '0.3rem' }}>
                  {t('game.helpSectionControls')}
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Courier New, monospace' }}>
                  <tbody>
                    {[
                      ['WASD / ←↑↓→', t('game.helpMove')],
                      ['C',            t('game.helpColor')],
                      ['G',            t('game.helpGrid')],
                      ['Space',        t('game.helpCreate')],
                      ['Delete',       t('game.helpDelete')],
                    ].map(([key, desc]) => (
                      <tr key={key}>
                        <td style={{ whiteSpace: 'nowrap', color: '#fff', fontWeight: 'bold', paddingRight: '1.25rem', paddingBottom: '0.35rem', verticalAlign: 'top' }}>{key}</td>
                        <td style={{ color: 'rgba(255,255,255,0.65)', paddingBottom: '0.35rem' }}>{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.6rem', letterSpacing: '1px', marginBottom: '0.6rem', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.12)', paddingBottom: '0.3rem' }}>
                  {t('game.helpSectionHud')}
                </h3>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {[
                    t('game.helpHudDashboard'),
                    t('game.helpHudCanvas'),
                    t('game.helpHudColor'),
                    t('game.helpHudPoint'),
                    t('game.helpHudSwitch'),
                    t('game.helpHudZone'),
                    t('game.helpHudGrid'),
                    t('game.helpHudShare'),
                    t('game.helpHudWall'),
                    t('game.helpHudDelete'),
                  ].map((item, i) => (
                    <li key={i} style={{ padding: '0.25rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.65)' }}>
                      <span style={{ color: 'rgba(255,255,255,0.30)', marginRight: '0.5rem' }}>›</span>{item}
                    </li>
                  ))}
                </ul>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.6rem', letterSpacing: '1px', marginBottom: '0.6rem', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.12)', paddingBottom: '0.3rem' }}>
                  {t('game.helpSectionMechanics')}
                </h3>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {[
                    t('game.helpMechMove'),
                    t('game.helpMechRate'),
                    t('game.helpMechSync'),
                    t('game.helpMechCreate'),
                  ].map((item, i) => (
                    <li key={i} style={{ padding: '0.25rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.65)' }}>
                      <span style={{ color: 'rgba(255,255,255,0.30)', marginRight: '0.5rem' }}>›</span>{item}
                    </li>
                  ))}
                </ul>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.6rem', letterSpacing: '1px', marginBottom: '0.6rem', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.12)', paddingBottom: '0.3rem' }}>
                  {t('game.helpSectionColors')}
                </h3>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {[
                    t('game.helpColorFree'),
                    t('game.helpColorPremium'),
                    t('game.helpColorCustom'),
                  ].map((item, i) => (
                    <li key={i} style={{ padding: '0.25rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.65)' }}>
                      <span style={{ color: 'rgba(255,255,255,0.30)', marginRight: '0.5rem' }}>›</span>{item}
                    </li>
                  ))}
                </ul>
              </section>

              <section style={{ marginBottom: '0.5rem' }}>
                <h3 style={{ fontSize: '0.6rem', letterSpacing: '1px', marginBottom: '0.6rem', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.12)', paddingBottom: '0.3rem' }}>
                  {t('game.helpSectionZones')}
                </h3>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {[
                    t('game.helpZoneDesc'),
                    t('game.helpZoneWarning'),
                    t('game.helpZoneFree'),
                    t('game.helpZoneWall'),
                  ].map((item, i) => (
                    <li key={i} style={{ padding: '0.25rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.65)' }}>
                      <span style={{ color: 'rgba(255,255,255,0.30)', marginRight: '0.5rem' }}>›</span>{item}
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}

          {/* Contacts */}
          {tab === 'contacts' && (
            <div style={{ fontSize: '0.75rem', lineHeight: '1.9', color: 'var(--px-text)' }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <span className="auth-form__label" style={{ display: 'block', marginBottom: '0.25rem' }}>
                  {t('about.emailLabel')}
                </span>
                <a
                  href="mailto:support@wearepixels.ru"
                  style={{ color: 'var(--px-accent)', textDecoration: 'none', fontSize: '0.75rem' }}
                >
                  support@wearepixels.ru
                </a>
              </div>
              {process.env.REACT_APP_SOCIAL_TELEGRAM && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <span className="auth-form__label" style={{ display: 'block', marginBottom: '0.25rem' }}>
                    {t('about.telegramLabel')}
                  </span>
                  <a
                    href={process.env.REACT_APP_SOCIAL_TELEGRAM}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--px-accent)', textDecoration: 'none', fontSize: '0.75rem' }}
                  >
                    {process.env.REACT_APP_SOCIAL_TELEGRAM.replace(/^https?:\/\/(t\.me\/)?/, '@').replace(/\/$/, '')}
                  </a>
                </div>
              )}
              <div>
                <span className="auth-form__label" style={{ display: 'block', marginBottom: '0.25rem' }}>
                  {t('about.legalLabel')}
                </span>
                <span style={{ color: 'var(--px-text-dim)', fontSize: '0.7rem' }}>
                  {t('about.legalName')}
                </span>
              </div>
            </div>
          )}

          {/* Oferta */}
          {tab === 'oferta' && (
            <div style={{ fontSize: '0.72rem', lineHeight: '1.7', color: 'var(--px-text)', maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
              <h2 style={{ fontSize: '0.75rem', letterSpacing: '1px', marginBottom: '0.5rem', color: 'var(--px-accent)', textAlign: 'center' }}>
                ПУБЛИЧНАЯ ОФЕРТА
              </h2>
              <p style={{ textAlign: 'center', marginBottom: '0.5rem', color: 'var(--px-text-dim)' }}>
                на оказание услуг доступа к платформе «We Are Pixels»
              </p>
              <p style={{ textAlign: 'center', marginBottom: '1.5rem', color: 'var(--px-text-dim)', fontSize: '0.66rem' }}>
                В редакции от 14.05.2026 г.
              </p>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>1. Общие положения</h3>
                <p style={{ marginBottom: '0.5rem' }}>1.1. Настоящий документ (далее — «Оферта» или «Договор») представляет собой официальное публичное предложение плательщика налога на профессиональный доход Чернокалова Александра Александровича (далее — «Исполнитель») заключить договор об оказании информационно-развлекательных услуг с любым физическим лицом (далее — «Заказчик» или «Пользователь») на изложенных ниже условиях.</p>
                <p style={{ marginBottom: '0.5rem' }}>1.2. В соответствии с п. 2 ст. 437 Гражданского кодекса Российской Федерации (ГК РФ), в случае принятия изложенных ниже условий и совершения конклюдентных действий Договор считается заключенным.</p>
                <p style={{ marginBottom: '0.5rem' }}>1.3. Акцепт Оферты — полное и безоговорочное принятие условий настоящей Оферты. Акцептом признается любое из следующих действий Заказчика:</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.5rem' }}>
                  <li>Регистрация или авторизация учетной записи на Сайте;</li>
                  <li>Фактическое использование функционала Сайта (в т.ч. управление пикселями на холсте);</li>
                  <li>Оплата Услуг Исполнителя.</li>
                </ul>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>2. Термины и определения</h3>
                <p style={{ marginBottom: '0.4rem' }}><strong>Сайт Исполнителя (Платформа)</strong> — веб-сайт, размещенный в сети Интернет по адресу: wearepixels.ru, включая все его поддомены и внутренние страницы.</p>
                <p style={{ marginBottom: '0.4rem' }}><strong>Личный кабинет</strong> — защищенный раздел Платформы, создаваемый при регистрации Заказчика, предназначенный для хранения данных, управления Услугами и отслеживания статуса подписок и бронирований.</p>
                <p style={{ marginBottom: '0.4rem' }}><strong>Услуги</strong> — предоставление Заказчику доступа к функциональным возможностям Платформы (как на безвозмездной, так и на платной основе).</p>
                <p style={{ marginBottom: '0.4rem' }}><strong>Базовый доступ</strong> — безвозмездная услуга, включающая возможность наблюдать за Платформой и управлять одним пикселем базового цвета.</p>
                <p style={{ marginBottom: '0.4rem' }}><strong>Платные услуги</strong> — предоставление расширенного функционала (премиум-цвета, подписки, бронирование зон) на возмездной основе.</p>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>3. Предмет Договора</h3>
                <p style={{ marginBottom: '0.5rem' }}>3.1. Исполнитель обязуется оказывать Заказчику Услуги по предоставлению доступа к Платформе, а Заказчик обязуется принимать эти Услуги и, в случае выбора Платных услуг, своевременно их оплачивать.</p>
                <p style={{ marginBottom: '0.5rem' }}>3.2. В состав Платных услуг могут входить:</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.75rem' }}>
                  <li><strong>«Магазин цветов — разовая покупка»</strong>: предоставление бессрочного права использования одного из 14 премиальных цветов для окрашивания своего пикселя.</li>
                  <li style={{ marginTop: '0.4rem' }}><strong>«Магазин цветов — подписка»</strong>: предоставление на срок 30 (тридцать) календарных дней доступа ко всему набору премиум-цветов, а также к вводу произвольного цвета в формате HEX. При оплате последующих периодов срок действия подписки суммируется.</li>
                  <li style={{ marginTop: '0.4rem' }}><strong>«Бронирование зоны»</strong>: резервирование прямоугольной области игрового мира, внутри которой только пиксели владельца зоны не имеют ограничений по времени нахождения. Пиксели иных пользователей удаляются из зоны автоматически по истечении периода бездействия.</li>
                </ul>
                <p style={{ marginBottom: '0.5rem' }}>3.3. Параметры зоны:</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.5rem' }}>
                  <li><strong>Стоимость зоны</strong>: зависит от её размера и удаленности центра от начала координат (0, 0), согласно тарифам на Сайте.</li>
                  <li><strong>Бесплатная зона</strong>: зоны, центр которых удален более чем на 3 000 единиц от начала координат, могут предоставляться бесплатно (не более 1 зоны на один Личный кабинет).</li>
                  <li><strong>Срок действия платной зоны</strong>: 30 (тридцать) календарных дней с момента успешной оплаты.</li>
                </ul>
                <p style={{ marginBottom: '0.5rem' }}>3.4. Действующие тарифы (цены указаны в российских рублях, НДС не облагается — налог на профессиональный доход):</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.5rem' }}>
                  <li><strong>Премиум-цвет (разовая покупка)</strong> — 9 ₽ за один цвет, право пользования бессрочное.</li>
                  <li style={{ marginTop: '0.4rem' }}><strong>Подписка «Премиум»</strong> (весь набор премиум-цветов + ввод произвольного цвета HEX) — 199 ₽ за 30 календарных дней.</li>
                  <li style={{ marginTop: '0.4rem' }}><strong>Бронирование зоны</strong> — стоимость рассчитывается как «цена за клетку × площадь зоны (клеток)» за 30 дней. Цена за клетку зависит от удаленности центра зоны от точки (0, 0):
                    <ul style={{ paddingLeft: '1.25rem', marginTop: '0.3rem' }}>
                      <li>до 100 ед. — 0,05 ₽ за клетку;</li>
                      <li>101–500 ед. — 0,03 ₽ за клетку;</li>
                      <li>501–1 500 ед. — 0,015 ₽ за клетку;</li>
                      <li>1 501–3 000 ед. — 0,005 ₽ за клетку;</li>
                      <li>более 3 000 ед. — бесплатно (приграничная зона, не более 1 на аккаунт, размер до 100×100 клеток).</li>
                    </ul>
                  </li>
                </ul>
                <p style={{ marginBottom: '0.5rem', color: 'var(--px-text-dim)', fontSize: '0.68rem' }}>3.5. Точная стоимость конкретной зоны рассчитывается автоматически и отображается в Личном кабинете перед оплатой. Исполнитель вправе изменять тарифы в порядке п. 4.2.2; изменение не затрагивает уже оплаченные периоды.</p>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>4. Права и обязанности Сторон</h3>
                <p style={{ marginBottom: '0.4rem', fontWeight: 'bold' }}>4.1. Исполнитель обязуется:</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.75rem' }}>
                  <li>4.1.1. Обеспечить Заказчику доступ к Платформе и Личному кабинету.</li>
                  <li>4.1.2. Соблюдать конфиденциальность данных Заказчика в соответствии с Политикой обработки персональных данных.</li>
                </ul>
                <p style={{ marginBottom: '0.4rem', fontWeight: 'bold' }}>4.2. Исполнитель имеет право:</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.75rem' }}>
                  <li>4.2.1. Временно приостанавливать работу Платформы для проведения технических или профилактических работ.</li>
                  <li>4.2.2. В одностороннем порядке изменять стоимость Платных услуг с публикацией новых тарифов на Сайте. Изменение тарифов не влияет на уже оплаченные периоды.</li>
                  <li>4.2.3. В любой момент удалять с холста контент, который нарушает законодательство РФ (экстремистская символика, оскорбления, порнография и т.д.), без предварительного уведомления пользователей, чьи пиксели образовали данный контент.</li>
                  <li>4.2.4. Блокировать Личный кабинет Заказчика в случае выявления нарушений п. 4.3 настоящей Оферты без возврата уплаченных средств.</li>
                </ul>
                <p style={{ marginBottom: '0.4rem', fontWeight: 'bold' }}>4.3. Заказчик обязуется:</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.5rem' }}>
                  <li>4.3.1. Использовать Платформу исключительно в личных некоммерческих целях.</li>
                  <li>4.3.2. Не использовать программные средства (скрипты, боты, автокликеры и т.д.) для автоматизации действий на Платформе (управления пикселем, массового окрашивания и т.д.), если это прямо не разрешено функционалом Сайта.</li>
                  <li>4.3.3. Не участвовать в координации действий, направленных на создание изображений, нарушающих законодательство РФ, пропагандирующих ненависть, насилие, содержащих нецензурную брань.</li>
                  <li>4.3.4. Не пытаться нарушить работоспособность серверов Платформы, не использовать уязвимости программного кода Сайта.</li>
                </ul>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>5. Интеллектуальная собственность и Пользовательский контент</h3>
                <p style={{ marginBottom: '0.5rem' }}>5.1. Платформа, её дизайн, программный код и алгоритмы являются интеллектуальной собственностью Исполнителя. Заказчику предоставляется ограниченное, неэксклюзивное, непередаваемое право использования интерфейса Сайта.</p>
                <p style={{ marginBottom: '0.5rem' }}>5.2. Визуальные изображения, создаваемые совместными усилиями Пользователей на Платформе, являются результатом коллективного творчества. Управляя пикселем, Заказчик предоставляет Исполнителю безвозмездное, бессрочное право использовать получившиеся изображения в маркетинговых, рекламных и информационных целях без указания авторства.</p>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>6. Порядок оплаты услуг. Политика возвратов</h3>
                <p style={{ marginBottom: '0.5rem' }}>6.1. Оплата Услуг осуществляется в безналичном порядке на условиях 100% предоплаты с использованием электронных средств платежа, доступных в интерфейсе интегрированного платежного сервиса.</p>
                <p style={{ marginBottom: '0.5rem' }}>6.2. Обязанность Заказчика по оплате считается исполненной, а Услуга считается оплаченной с момента получения Исполнителем от платежного агрегатора или банка подтверждения об успешной транзакции.</p>
                <p style={{ marginBottom: '0.5rem' }}>6.3. Исполнитель не осуществляет сбор, обработку и хранение данных банковских карт или иных платежных реквизитов Заказчика. Все операции по обработке платежей осуществляются на защищенной стороне платежного партнера.</p>
                <p style={{ marginBottom: '0.5rem' }}>6.4. Заказчик самостоятельно несет ответственность за правильность осуществления платежей.</p>
                <p style={{ marginBottom: '0.4rem', fontWeight: 'bold' }}>6.5. Автоматическое продление</p>
                <p style={{ marginBottom: '0.4rem' }}>6.5.1. При покупке Услуги «Магазин цветов — подписка» Заказчик вправе привязать банковскую карту и дать согласие на регулярное автоматическое списание денежных средств (рекуррентные платежи) за каждый последующий 30-дневный период.</p>
                <p style={{ marginBottom: '0.4rem' }}>6.5.2. Списание средств за следующий период производится автоматически за 24 часа до окончания текущего оплаченного периода.</p>
                <p style={{ marginBottom: '0.5rem' }}>6.5.3. Заказчик вправе в любой момент отменить автоматическое продление подписки в настройках Личного кабинета. Отмена должна быть произведена не позднее чем за 24 часа до окончания текущего периода во избежание списания за следующий месяц.</p>
                <p style={{ marginBottom: '0.4rem', fontWeight: 'bold' }}>6.6. Условия возврата денежных средств</p>
                <p style={{ marginBottom: '0.4rem' }}>6.6.1. В соответствии со ст. 429.4 ГК РФ при оплате доступа (подписки или бронирования зоны на срок) Заказчик обязан вносить платежи независимо от того, было ли затребовано им соответствующее исполнение: фактическое использование пикселя или зоны. Неиспользование функционала Сайта при наличии оплаченного доступа не является основанием для возврата денежных средств.</p>
                <p style={{ marginBottom: '0.4rem' }}>6.6.2. Услуга «Магазин цветов — разовая покупка» является предоставлением доступа к цифровому контенту. В силу специфики услуги она считается оказанной в полном объеме в момент разблокировки цвета в Личном кабинете Заказчика. Возврат средств за данную Услугу надлежащего качества не производится.</p>
                <p style={{ marginBottom: '0.4rem' }}>6.6.3. При досрочном отказе Заказчика от Услуг «Подписка» или «Бронирование зоны» Исполнитель не производит перерасчет и возврат денежных средств за неиспользованный остаток дней. Услуга по предоставлению серверных мощностей и резервированию пространства на Платформе считается фактически оказанной в момент активации. Заказчик сохраняет доступ к оплаченному функционалу до истечения оплаченного месяца.</p>
                <p style={{ marginBottom: '0.4rem' }}>6.6.4. Возврат денежных средств возможен исключительно в следующих случаях:</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.4rem' }}>
                  <li>Техническая ошибка платежной системы, повлекшая двойное или некорректное списание средств.</li>
                  <li>Доказанная техническая невозможность Исполнителя предоставить доступ к оплаченным функциям более 72 часов подряд по вине Исполнителя (за исключением форс-мажора).</li>
                </ul>
                <p style={{ marginBottom: '0.5rem' }}>6.6.5. Для оформления возврата в случаях, предусмотренных п. 6.6.4., Заказчик направляет мотивированное заявление на электронную почту Исполнителя. Исполнитель рассматривает заявление в течение 10 (десяти) календарных дней. В случае одобрения, возврат производится на те же платежные реквизиты, с которых была произведена оплата, в срок 10 рабочих дней.</p>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>7. Ответственность Сторон и Форс-мажор</h3>
                <p style={{ marginBottom: '0.5rem' }}>7.1. Платформа предоставляется на условиях «как есть». Исполнитель не гарантирует, что Платформа будет соответствовать субъективным ожиданиям Заказчика, работать непрерывно, и без ошибок.</p>
                <p style={{ marginBottom: '0.5rem' }}>7.2. Исполнитель не несет ответственности за технические сбои на стороне провайдеров связи, хостинг-провайдеров, платежных систем, а также за последствия DDoS-атак или несанкционированного доступа третьих лиц к Платформе.</p>
                <p style={{ marginBottom: '0.5rem' }}>7.3. Максимальная ответственность Исполнителя по любому из оснований ограничивается суммой, фактически уплаченной Заказчиком Исполнителю за Платные услуги за последние 30 (тридцать) дней.</p>
                <p style={{ marginBottom: '0.5rem' }}>7.4. Стороны освобождаются от ответственности за неисполнение обязательств вследствие обстоятельств непреодолимой силы (форс-мажор), включая, но не ограничиваясь: стихийные бедствия, акты государственных органов, эпидемии, сбои в магистральных сетях связи.</p>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>8. Разрешение споров</h3>
                <p style={{ marginBottom: '0.5rem' }}>8.1. Все споры и разногласия решаются путем переговоров. Обязателен досудебный претензионный порядок.</p>
                <p style={{ marginBottom: '0.5rem' }}>8.2. Претензия направляется Заказчиком на электронную почту Исполнителя. Срок рассмотрения претензии — 10 (десять) рабочих дней с момента получения.</p>
                <p style={{ marginBottom: '0.5rem' }}>8.3. При недостижении согласия спор подлежит рассмотрению в суде по месту нахождения Исполнителя.</p>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>9. Заключительные положения</h3>
                <p style={{ marginBottom: '0.5rem' }}>9.1. Настоящий Договор вступает в силу с момента совершения Заказчиком акцепта.</p>
                <p style={{ marginBottom: '0.5rem' }}>9.2. Исполнитель оставляет за собой право в любое время по своему усмотрению вносить изменения и дополнения в текст настоящей Оферты.</p>
                <p style={{ marginBottom: '0.5rem' }}>9.3. Новая редакция Оферты вступает в силу с момента её публикации на Сайте, если иное прямо не указано в тексте новой редакции. Продолжение использования Платформы Заказчиком после публикации изменений в Оферте признается юридически значимым действием, подтверждающим согласие Заказчика с условиями новой редакции Договора. В случае несогласия с измененными условиями Заказчик обязан немедленно прекратить использование Платформы и вправе расторгнуть Договор в одностороннем порядке путем удаления учетной записи.</p>
                <p style={{ marginBottom: '0.5rem' }}>9.4. Признание судом недействительным какого-либо положения настоящей Оферты не влечет недействительности остальных ее положений.</p>
                <p style={{ marginBottom: '0.5rem' }}>9.5. Вся коммуникация между Сторонами ведется посредством электронной почты.</p>
              </section>

              <section style={{ marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>10. Реквизиты исполнителя</h3>
                <p style={{ marginBottom: '0.25rem' }}>Чернокалов Александр Александрович</p>
                <p style={{ marginBottom: '0.25rem' }}>ИНН: 501848456950</p>
                <p style={{ marginBottom: '0.25rem' }}>E-mail: <a href="mailto:admin@wearepixels.ru" style={{ color: 'var(--px-accent)', textDecoration: 'none' }}>admin@wearepixels.ru</a></p>
              </section>
            </div>
          )}

          {/* Privacy */}
          {tab === 'privacy' && (
            <div style={{ fontSize: '0.72rem', lineHeight: '1.7', color: 'var(--px-text)', maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
              <h2 style={{ fontSize: '0.75rem', letterSpacing: '1px', marginBottom: '0.5rem', color: 'var(--px-accent)', textAlign: 'center' }}>
                ПОЛИТИКА ОБРАБОТКИ ПЕРСОНАЛЬНЫХ ДАННЫХ
              </h2>
              <p style={{ textAlign: 'center', marginBottom: '1.5rem', color: 'var(--px-text-dim)' }}>
                wearepixels.ru
              </p>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>1. Общие положения</h3>
                <p style={{ marginBottom: '0.5rem' }}>Настоящая Политика определяет порядок обработки персональных данных пользователей сайта wearepixels.ru (далее — Сайт) в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных».</p>
                <p style={{ marginBottom: '0.5rem' }}>Оператор персональных данных: We Are Pixels, контактный e-mail: <a href="mailto:admin@wearepixels.ru" style={{ color: 'var(--px-accent)', textDecoration: 'none' }}>admin@wearepixels.ru</a>.</p>
                <p>Используя Сайт, вы соглашаетесь с условиями настоящей Политики. Если вы не согласны — пожалуйста, прекратите использование Сайта.</p>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>2. Какие данные мы собираем</h3>
                <p style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>2.1. Данные, предоставляемые пользователем</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.75rem' }}>
                  <li><strong>Адрес электронной почты</strong> — единственный персональный идентификатор, собираемый при регистрации. Используется для входа в аккаунт, отправки письма подтверждения и связи по вопросам поддержки.</li>
                </ul>
                <p style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>2.2. Данные, собираемые автоматически (аналитика)</p>
                <p style={{ marginBottom: '0.5rem' }}>Мы используем сервисы веб-аналитики для улучшения работы Сайта. Все данные передаются <strong>в обезличенном виде</strong> и не позволяют идентифицировать конкретного пользователя:</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.5rem' }}>
                  <li><strong>Яндекс.Метрика</strong> (ООО «Яндекс», Россия) — посещаемость страниц, источники трафика, технические характеристики браузера и устройства. Данные обрабатываются согласно <a href="https://yandex.ru/legal/confidential/" target="_blank" rel="noreferrer" style={{ color: 'var(--px-accent)', textDecoration: 'none' }}>политике конфиденциальности Яндекса</a>. IP-адрес передаётся в усечённом виде.</li>
                  <li style={{ marginTop: '0.4rem' }}><strong>Google Analytics</strong> (Google LLC, США) — аналогичная статистика использования. Данные обрабатываются согласно <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--px-accent)', textDecoration: 'none' }}>политике конфиденциальности Google</a>. Используется режим анонимизации IP.</li>
                </ul>
                <p style={{ color: 'var(--px-text-dim)', fontSize: '0.68rem' }}>Мы не собираем платёжные данные — они обрабатываются напрямую платёжным сервисом Robokassa.</p>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>3. Цели обработки данных</h3>
                <ul style={{ paddingLeft: '1.25rem' }}>
                  <li>Идентификация пользователя и предоставление доступа к аккаунту.</li>
                  <li>Подтверждение адреса электронной почты.</li>
                  <li>Направление уведомлений, связанных с работой Сайта (только технические, без рекламных рассылок).</li>
                  <li>Анализ посещаемости и улучшение качества Сайта (обезличенная аналитика).</li>
                </ul>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>4. Передача данных третьим лицам</h3>
                <p style={{ marginBottom: '0.5rem' }}>Персональные данные не продаются и не передаются третьим лицам в коммерческих целях. Ограниченная передача возможна только в следующих случаях:</p>
                <ul style={{ paddingLeft: '1.25rem' }}>
                  <li>Сервисы аналитики (Яндекс.Метрика, Google Analytics) — исключительно обезличенные данные.</li>
                  <li>По требованию уполномоченных государственных органов в порядке, предусмотренном законодательством РФ.</li>
                </ul>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>5. Хранение и защита данных</h3>
                <p style={{ marginBottom: '0.5rem' }}>Email-адрес хранится в защищённой базе данных. Пароли хранятся исключительно в виде хеша (bcrypt) и не могут быть восстановлены.</p>
                <p>Данные хранятся до момента удаления аккаунта пользователем или по его запросу.</p>
              </section>

              <section style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>6. Права пользователя</h3>
                <p style={{ marginBottom: '0.5rem' }}>В соответствии с ФЗ-152 вы вправе:</p>
                <ul style={{ paddingLeft: '1.25rem' }}>
                  <li>Получить информацию об обрабатываемых персональных данных.</li>
                  <li>Потребовать исправления неточных данных.</li>
                  <li>Потребовать удаления аккаунта и связанных данных.</li>
                  <li>Отозвать согласие на обработку персональных данных.</li>
                </ul>
                <p style={{ marginTop: '0.5rem' }}>Для реализации прав обратитесь по адресу: <a href="mailto:admin@wearepixels.ru" style={{ color: 'var(--px-accent)', textDecoration: 'none' }}>admin@wearepixels.ru</a>.</p>
              </section>

              <section style={{ marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '0.72rem', marginBottom: '0.5rem', color: 'var(--px-accent)' }}>7. Изменения политики</h3>
                <p>Мы вправе обновлять настоящую Политику. Актуальная версия всегда доступна на этой странице. Продолжение использования Сайта после публикации изменений означает согласие с новой редакцией.</p>
              </section>
            </div>
          )}

          <div className="auth-form__footer" style={{ marginTop: '1.5rem' }}>
            <Link to="/">
              <button className="btn-transparent" style={{ fontSize: '0.6rem' }}>
                ← {t('common.backToMap')}
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
